import { useEffect, useMemo, useState } from 'react';
import ModalShell from './ModalShell';
import CorporateMultiSelect from './CorporateMultiSelect';
import CandidateSectionHeader from './CandidateSectionHeader';
import { CONTACT_CHANNELS, GENDER_OPTIONS, WORKING_MODES } from '../utils/candidateFormValidation';
import {
  CANDIDATE_INPUT_CLASS,
  CANDIDATE_SALARY_GRID,
  CANDIDATE_SECTION_CLASS,
  CORPORATE
} from '../constants/candidateFormStyles';
import {
  EMPTY_CANDIDATE_ADVANCED_FILTERS,
  EMPTY_CANDIDATE_FILTER_FACETS,
  countActiveAdvancedFilters,
  countAdvancedFilterMatches,
  formatAvailabilityLabel,
  mergeLanguageFilterOptions,
  mergeMaritalStatusFilterOptions
} from '../utils/candidateFilters';
import { mergeEducationQualificationFilterOptions } from '../constants/educationOptions';

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const GENDER_LABELS = Object.fromEntries(
  GENDER_OPTIONS.filter(o => o.value).map(o => [o.value, o.label])
);

const WORKING_MODE_LABELS = Object.fromEntries(WORKING_MODES.map(o => [o.id, o.label]));
const CONTACT_CHANNEL_LABELS = Object.fromEntries(CONTACT_CHANNELS.map(o => [o.id, o.label]));

function formatLabel(value, labelMap) {
  const key = String(value ?? '');
  return labelMap?.[key] ?? labelMap?.[value] ?? key;
}

function optionsFromFacet(values, labelMap = null) {
  return (values || [])
    .filter(v => v != null && String(v).trim() !== '' && String(v).trim() !== '—')
    .map(value => {
      const strValue = String(value).trim();
      return {
        value: strValue,
        label: String(formatLabel(strValue, labelMap) || strValue)
      };
    });
}

function availabilityOptionsFromFacet(values) {
  const labelMap = Object.fromEntries(
    (values || [])
      .filter(v => v != null && String(v).trim() !== '' && String(v).trim() !== '—')
      .map(v => {
        const s = String(v).trim();
        return [s, formatAvailabilityLabel(s) || s];
      })
  );
  return optionsFromFacet(values, labelMap);
}

function MatchBadge({ count }) {
  const label = count === 1 ? 'Match' : 'Matches';
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--ds-primary)/0.3)] bg-[hsl(var(--ds-primary)/0.08)] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--ds-primary))] shadow-sm"
      aria-live="polite"
      aria-label={`${count} ${label.toLowerCase()}`}
    >
      <span className="inline-flex min-w-[1.375rem] h-6 items-center justify-center rounded-full border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] px-2 text-[11px] font-bold leading-none text-[hsl(var(--ds-primary))]">
        {count}
      </span>
      {label}
    </span>
  );
}

function FilterSection({ title, hint, children, gridClass = 'grid gap-4 md:grid-cols-2' }) {
  return (
    <div className={CANDIDATE_SECTION_CLASS}>
      <CandidateSectionHeader title={title} hint={hint} />
      <div className={gridClass}>{children}</div>
    </div>
  );
}

/** Floating label + bordered control (same as Profile / marital status filter). */
function FilterFieldWithLabel({ label, children, className = '', filled = true }) {
  return (
    <div
      className={`field-with-label min-w-0 ${filled ? 'qualification-filled' : ''} ${className}`.trim()}
    >
      {children}
      <label>{label}</label>
    </div>
  );
}

function FilterTextInput({ label, className = '', ...inputProps }) {
  return (
    <FilterFieldWithLabel label={label} className={className}>
      <input className={`${CANDIDATE_INPUT_CLASS} w-full`} placeholder=" " {...inputProps} />
    </FilterFieldWithLabel>
  );
}

function ProfileMultiSelect({
  options,
  values,
  onChange,
  emptyLabel,
  menuMinWidth,
  searchable = true
}) {
  return (
    <CorporateMultiSelect
      options={options}
      values={values}
      onChange={onChange}
      emptyLabel={emptyLabel}
      searchable={searchable}
      menuMinWidth={menuMinWidth}
      profileFieldStyle
    />
  );
}

function YesNoSelect({ values, onChange, emptyLabel = 'Any' }) {
  return (
    <CorporateMultiSelect
      options={YES_NO_OPTIONS}
      values={values}
      onChange={onChange}
      emptyLabel={emptyLabel}
      searchable={false}
      profileFieldStyle
    />
  );
}

export default function CandidateAdvancedFilterModal({
  open,
  filters,
  facets = EMPTY_CANDIDATE_FILTER_FACETS,
  facetsLoading = false,
  candidatePool = [],
  getExperienceYears,
  expertiseCatalog = [],
  onRefreshFacets,
  onClose,
  onApply
}) {
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  useEffect(() => {
    if (open) onRefreshFacets?.();
  }, [open, onRefreshFacets]);

  const options = useMemo(
    () => ({
      locations: optionsFromFacet(facets.locations),
      genders: optionsFromFacet(facets.genders, GENDER_LABELS),
      maritalStatuses: mergeMaritalStatusFilterOptions(facets.maritalStatuses),
      states: optionsFromFacet(facets.states),
      cities: optionsFromFacet(facets.cities),
      countries: optionsFromFacet(facets.countries),
      workingModes: optionsFromFacet(facets.workingModes, WORKING_MODE_LABELS),
      contactChannels: optionsFromFacet(facets.contactChannels, CONTACT_CHANNEL_LABELS),
      educationDegrees: mergeEducationQualificationFilterOptions(facets.educationDegrees),
      educationInstitutions: optionsFromFacet(facets.educationInstitutions),
      companies: optionsFromFacet(facets.companies),
      designations: optionsFromFacet(facets.designations),
      profileHeadings: optionsFromFacet(facets.profileHeadings),
      availabilities: availabilityOptionsFromFacet(facets.availabilities),
      skills: optionsFromFacet(facets.skills),
      technicalSkills: optionsFromFacet(facets.technicalSkills),
      areasOfExpertise: optionsFromFacet(facets.areasOfExpertise),
      languages: mergeLanguageFilterOptions(facets.languages)
    }),
    [facets]
  );

  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const hasDraftFilters = countActiveAdvancedFilters(draft) > 0;

  const matchCount = useMemo(
    () =>
      countAdvancedFilterMatches(candidatePool, draft, {
        getExperienceYears: getExperienceYears || (() => null),
        expertiseCatalog
      }),
    [candidatePool, draft, getExperienceYears, expertiseCatalog]
  );

  if (!open) return null;

  const profileHint = facetsLoading
    ? 'Loading candidate profiles…'
    : 'Options come from saved candidate profiles and update when profiles change.';

  return (
    <ModalShell
      title="Filters"
      subtitle={profileHint}
      onClose={onClose}
      maxWidth="max-w-4xl"
      corporate
      contentClassName="candidate-filter-modal candidate-form"
      footer={
        <div className="flex w-full items-center gap-3 flex-wrap">
          <button
            type="button"
            className={CORPORATE.btnSecondary}
            onClick={() => setDraft({ ...EMPTY_CANDIDATE_ADVANCED_FILTERS })}
          >
            Reset
          </button>
          {hasDraftFilters && <MatchBadge count={matchCount} />}
          <div className="flex items-center gap-2 ml-auto">
            <button type="button" className={CORPORATE.btnSecondary} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2.5 text-sm font-medium rounded-xl hover:bg-[hsl(var(--ds-primary)/0.08)] shrink-0 shadow-sm"
              onClick={() => onApply(draft)}
            >
              Apply
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <FilterSection
          title="Master search"
          hint="Search any keyword across the full candidate profile, including resume text extracted from PDF, Word, and other uploaded documents."
          gridClass="grid gap-4"
        >
          <FilterTextInput
            label="Search entire profile"
            className="md:col-span-2"
            value={draft.masterSearch}
            onChange={e => setField('masterSearch', e.target.value)}
            placeholder="Name, skills, education, resume keywords…"
          />
        </FilterSection>

        <FilterSection title="Photo & Resume">
          <FilterFieldWithLabel label="Profile photo">
            <YesNoSelect values={draft.hasPhoto} onChange={v => setField('hasPhoto', v)} emptyLabel="Any" />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Resume on file">
            <YesNoSelect values={draft.hasResume} onChange={v => setField('hasResume', v)} emptyLabel="Any" />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Profile Heading">
          <FilterFieldWithLabel label="Profile heading" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.profileHeadings}
              values={draft.profileHeadings}
              onChange={v => setField('profileHeadings', v)}
              emptyLabel="Any heading"
              menuMinWidth={320}
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Basic Information">
          <FilterTextInput
            label="Candidate ID"
            value={draft.candidateId}
            onChange={e => setField('candidateId', e.target.value)}
          />
          <FilterTextInput
            label="Name"
            value={draft.name}
            onChange={e => setField('name', e.target.value)}
          />
          <FilterTextInput
            label="Email"
            value={draft.email}
            onChange={e => setField('email', e.target.value)}
          />
          <FilterTextInput
            label="Phone"
            value={draft.phone}
            onChange={e => setField('phone', e.target.value)}
          />
          <FilterFieldWithLabel label="LinkedIn profile">
            <YesNoSelect values={draft.hasLinkedin} onChange={v => setField('hasLinkedin', v)} emptyLabel="Any" />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Website">
            <YesNoSelect values={draft.hasWebsite} onChange={v => setField('hasWebsite', v)} emptyLabel="Any" />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Personal Details">
          <FilterFieldWithLabel label="Gender">
            <ProfileMultiSelect
              options={options.genders}
              values={draft.gender}
              onChange={v => setField('gender', v)}
              emptyLabel="Any gender"
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Marital status">
            <ProfileMultiSelect
              options={options.maritalStatuses}
              values={draft.maritalStatus}
              onChange={v => setField('maritalStatus', v)}
              emptyLabel="Any status"
              searchable={false}
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="State">
            <ProfileMultiSelect
              options={options.states}
              values={draft.states}
              onChange={v => setField('states', v)}
              emptyLabel="Any state"
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="City">
            <ProfileMultiSelect
              options={options.cities}
              values={draft.cities}
              onChange={v => setField('cities', v)}
              emptyLabel="Any city"
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Country">
            <ProfileMultiSelect
              options={options.countries}
              values={draft.countries}
              onChange={v => setField('countries', v)}
              emptyLabel="Any country"
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection
          title="Educational Details"
          hint="Degree filter uses the candidate's highest qualification only (e.g. 12th Standard excludes graduates)"
        >
          <FilterFieldWithLabel label="Highest qualification">
            <ProfileMultiSelect
              options={options.educationDegrees}
              values={draft.educationDegrees}
              onChange={v => setField('educationDegrees', v)}
              emptyLabel="Any degree"
              menuMinWidth={320}
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Institution">
            <ProfileMultiSelect
              options={options.educationInstitutions}
              values={draft.educationInstitutions}
              onChange={v => setField('educationInstitutions', v)}
              emptyLabel="Any institution"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Professional Details">
          <FilterFieldWithLabel label="Company">
            <ProfileMultiSelect
              options={options.companies}
              values={draft.companies}
              onChange={v => setField('companies', v)}
              emptyLabel="Any company"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Designation">
            <ProfileMultiSelect
              options={options.designations}
              values={draft.designations}
              onChange={v => setField('designations', v)}
              emptyLabel="Any designation"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Fresher">
            <YesNoSelect values={draft.fresher} onChange={v => setField('fresher', v)} emptyLabel="Any" />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection
          title="Work preferences"
          hint="Matches saved working modes, contact channels, and preferred locations"
        >
          <FilterFieldWithLabel label="Preferred working mode">
            <ProfileMultiSelect
              options={options.workingModes}
              values={draft.workingModes}
              onChange={v => setField('workingModes', v)}
              emptyLabel="Any mode"
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Preferred contact channels">
            <ProfileMultiSelect
              options={options.contactChannels}
              values={draft.contactChannels}
              onChange={v => setField('contactChannels', v)}
              emptyLabel="Any channel"
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Preferred work location" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.locations}
              values={draft.locations}
              onChange={v => setField('locations', v)}
              emptyLabel="Any location"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Core competencies & technical skills" hint="Candidates with any of the selected items">
          <FilterFieldWithLabel label="Core competencies (any match)" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.skills}
              values={draft.specificSkills}
              onChange={v => setField('specificSkills', v)}
              emptyLabel="Any core competency"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
          <FilterFieldWithLabel label="Technical skills (any match)" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.technicalSkills}
              values={draft.specificTechnicalSkills}
              onChange={v => setField('specificTechnicalSkills', v)}
              emptyLabel="Any technical skill"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Area of expertise" hint="Candidates in any of the selected domains">
          <FilterFieldWithLabel label="Area of expertise (any match)" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.areasOfExpertise}
              values={draft.areasOfExpertise}
              onChange={v => setField('areasOfExpertise', v)}
              emptyLabel="Any domain"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Salary & Experience" gridClass={CANDIDATE_SALARY_GRID}>
          <FilterTextInput
            label="Current salary min (INR, per annum)"
            inputMode="numeric"
            value={draft.currentSalaryMin}
            onChange={e => setField('currentSalaryMin', e.target.value.replace(/\D/g, ''))}
          />
          <FilterTextInput
            label="Current salary max (INR, per annum)"
            inputMode="numeric"
            value={draft.currentSalaryMax}
            onChange={e => setField('currentSalaryMax', e.target.value.replace(/\D/g, ''))}
          />
          <FilterTextInput
            label="Expected salary min (INR, per annum)"
            inputMode="numeric"
            value={draft.expectedSalaryMin}
            onChange={e => setField('expectedSalaryMin', e.target.value.replace(/\D/g, ''))}
          />
          <FilterTextInput
            label="Expected salary max (INR, per annum)"
            inputMode="numeric"
            value={draft.expectedSalaryMax}
            onChange={e => setField('expectedSalaryMax', e.target.value.replace(/\D/g, ''))}
          />
          <FilterTextInput
            label="Experience min (years)"
            type="number"
            min="0"
            value={draft.experienceMin}
            onChange={e => setField('experienceMin', e.target.value)}
          />
          <FilterTextInput
            label="Experience max (years)"
            type="number"
            min="0"
            value={draft.experienceMax}
            onChange={e => setField('experienceMax', e.target.value)}
          />
          <FilterFieldWithLabel label="Notice period / availability" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.availabilities}
              values={draft.availabilities}
              onChange={v => setField('availabilities', v)}
              emptyLabel="Any notice period"
            />
          </FilterFieldWithLabel>
        </FilterSection>

        <FilterSection title="Languages Known" hint="Candidates who listed any of these languages">
          <FilterFieldWithLabel label="Languages (any match)" className="md:col-span-2">
            <ProfileMultiSelect
              options={options.languages}
              values={draft.languages}
              onChange={v => setField('languages', v)}
              emptyLabel="Any language"
              menuMinWidth={280}
            />
          </FilterFieldWithLabel>
        </FilterSection>
      </div>
    </ModalShell>
  );
}
